/**
 *
 * Login
 *
 */

import React, { memo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { Link } from 'react-router-dom';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import {
  makeSelectUser,
  makeSelectUserLoading,
  makeSelectUserError,
  makeSelectLoginForm,
  makeSelectAuthenticated,
} from './selectors';
import { flushState, loginUser } from './actions';

// utils
// NOT DELETE!!
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import {
  appHelper,
  // Status Bar helper
  appStatusBar,
} from 'appCapacitor/helpers';
import useDeviceDetect from 'utils/useDeviceDetect';

// antd component
import { Form, Input, Button, Alert } from 'antd';

// layout components
import Preloader from 'components/Preloader';
import AppVersionInformation from 'components/AppVersionInformation';

// context
import { withUser, useSetUserData } from 'engine/Contexts/User.context';

// assets
import CustomIcons from 'components/CustomIcons';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';


export function Login({
  currentUser,
  loading,
  error,
  authenticated,
  history,
  user,
  // actions
  loginUser,
  flushState,
}) {
  useInjectReducer({ key: 'login', reducer });
  useInjectSaga({ key: 'login', saga });

  const { isMobile } = useDeviceDetect();

  const {
    populateUser,
    isAuthenticated,
    setAutorization,
  } = user;

  const setUserData = useSetUserData();

  const isAuthed = isAuthenticated();
  const [spinner, setSpinner] = useState(true);

  const [loginForm] = Form.useForm();

  let isApp = false;
  if (!appHelper.isWeb || isMobile) {
    isApp = true;
  }

  let preloader = false;

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    preloader = setTimeout(() => setSpinner(false), 1500);
    // Anything in here is fired on component unmount.
    return () => {
      // this will clear setTimeout
      !!preloader && clearTimeout(preloader);
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  useEffect(() => {
    if ((!loading && authenticated) || (!loading && isAuthed)) {
      setAutorization(authenticated);
      setUserData(currentUser);
      populateUser(currentUser);
      // Set Status Bar
      (appHelper.isAndroid || appHelper.isIos) && appStatusBar.setStatusBarBackgroundColor();
      history.push('/');
    }
  }, [isAuthed, authenticated]);

  const onFinish = values => {
    loginUser(values, isApp);
  };

  const onFinishFailed = errorInfo => {
    console.error('Failed Login:', errorInfo);
  };

  const onValuesChangeCallback = useCallback((changedValues, allValues) => {
    // Email has to be only lowercase on creation and without accents
    if (!!changedValues?.username) {
      let newEmail = changedValues?.username.toLowerCase();
      newEmail = newEmail.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      loginForm.setFieldsValue({
        username: newEmail,
      });
    }
  }, []);


  // render function
  return (
    <>
      {spinner && <Preloader fullWidth />}
      {!spinner && (
        <>
          <div className={'login-page'}>
            <AppVersionInformation loading={loading} authenticated={authenticated} />
            <div className="login-form__column">
              <div className="login-form__header">
                <CustomIcons className="logo-login" type="logo" />
              </div>
              <Form
                name="login_form"
                form={loginForm}
                className="login-form__container"
                initialValues={{ remember: true }}
                onFinish={value => onFinish(value)}
                onFinishFailed={errorInfo => onFinishFailed(errorInfo)}
                onValuesChange={onValuesChangeCallback}
              >
                <Form.Item
                  name="username"
                  rules={[
                    {
                      required: true,
                      message: `Veuillez saisir votre adresse e-mail`,
                    },
                  ]}
                >
                  <Input size="large" placeholder="Adresse e-mail" />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: `Veuillez saisir votre mot de passe`,
                    },
                  ]}
                >
                  <Input.Password
                    className={'password'}
                    size="large"
                    placeholder="Mot de passe"
                    iconRender={visible =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    className="login-submit"
                    loading={loading}
                  >
                    Se connecter
                  </Button>
                </Form.Item>

                <div className="login-form__bottom">
                  {isMobile && (
                    <Link
                      to={'/signupparent'}
                      className="login-form-register"
                    >
                      Nouveau parent
                    </Link>
                  )}
                  <Link
                    to={'/reset'}
                    className="login-form-password"
                  >
                    Mot de passe oublié
                  </Link>
                </div>

                {error && (
                  <Alert
                    message="Erreur de connexion"
                    description={`Une erreur de connexion est survenue. Verifiez vos informations de connexion (Adresse e-mail et/ou Mot de passe). ${
                      error.message
                    }`}
                    type="error"
                    closable
                  />
                )}
              </Form>

            </div>
            <svg
                className="waves"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="0 24 150 28"
                preserveAspectRatio="none"
                shapeRendering="auto"
              >
                <defs>
                  <path
                    id="gentle-wave"
                    d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z"
                  />
                </defs>
                <g className="parallax">
                  <use
                    href="#gentle-wave"
                    x="48"
                    y="0"
                    fill="rgba(255,255,255,0.7"
                  />
                  <use
                    href="#gentle-wave"
                    x="48"
                    y="3"
                    fill="rgba(255,255,255,0.5)"
                  />
                  <use
                    href="#gentle-wave"
                    x="48"
                    y="5"
                    fill="rgba(255,255,255,0.3)"
                  />
                  <use href="#gentle-wave" x="48" y="7" fill="#fff" />
                </g>
              </svg>
          </div>
        </>
      )}
    </>
  );
}

Login.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  currentUser: makeSelectUser(),
  authenticated: makeSelectAuthenticated(),
  error: makeSelectUserError(),
  loading: makeSelectUserLoading(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loginUser: (loginValues, isApp) => dispatch(loginUser(loginValues, isApp)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
  withUser,
)(Login);
